import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { applyMeta } from "@/lib/seo";
import { isAdminHost } from "@/lib/utils";

// Route driven page titles + meta. Rendered once at the router root; on every
// navigation it writes the matching title, description, canonical and robots
// tags. Data pages (listing detail, host profile) upgrade the defaults with
// their loaded content via usePageMeta.
//
// Private app surfaces are noindex: they are login gated, so a crawler only
// ever sees the login redirect; noindex keeps them out of the index cleanly.

const PUBLIC_ROUTES = [
  [/^\/$/, {
    title: "Swipe Your Next Rental Home in Canada",
    description:
      "SwipRent is the swipe first rental marketplace. Browse apartments, condos and houses for rent across Canada, like the ones you love and match with listers instantly.",
  }],
  [/^\/pricing$/, {
    title: "Pricing and Plans",
    description:
      "Simple pricing for renters and listers on SwipRent. List a home, boost your reach and unlock renter screening with clear one time and monthly plans.",
  }],
  [/^\/how-it-works$/, {
    title: "How SwipRent Works",
    description:
      "Swipe right on homes you love, match with the lister and chat instantly. See how SwipRent turns the rental search into a two sided match.",
  }],
  [/^\/map$/, {
    title: "Rental Map | Browse Homes for Rent Near You",
    description:
      "Explore homes for rent on an interactive map of Canada. Filter by price, beds and city, then swipe the listings you like on SwipRent.",
  }],
  [/^\/hosts$/, {
    title: "Hosts and Agents Directory",
    description:
      "Meet the private owners and licensed realtors listing homes on SwipRent. Browse verified hosts and agents across Canada.",
  }],
  [/^\/hosts\/[^/]+$/, {
    title: "Host Profile",
    description: "View this host's listings and profile on SwipRent.",
  }],
  [/^\/listing\/[^/]+$/, {
    title: "Home for Rent",
    description: "View photos, pricing and details for this rental home on SwipRent.",
  }],
  [/^\/auth\/signup$/, {
    title: "Sign Up Free",
    description:
      "Create your free SwipRent account and start swiping homes for rent across Canada in under a minute.",
  }],
  [/^\/auth\/login$/, {
    title: "Log In",
    description: "Log in to SwipRent to keep swiping homes, check your matches and chat with listers.",
  }],
  [/^\/terms$/, {
    title: "Terms of Service",
    description: "The terms that govern your use of SwipRent, the swipe first rental marketplace.",
  }],
  [/^\/privacy$/, {
    title: "Privacy Policy",
    description: "How SwipRent collects, uses and protects your personal information.",
  }],
];

// Login gated app surfaces: indexed never, titled always (tabs, history,
// screen readers still benefit).
const PRIVATE_ROUTES = [
  [/^\/swipe$/, "Discover Homes"],
  [/^\/saved$/, "Saved Homes"],
  [/^\/likes$/, "Your Likes"],
  [/^\/liked-you$/, "Liked You"],
  [/^\/matches$/, "Matches"],
  [/^\/messages$/, "Messages"],
  [/^\/chat\//, "Chat"],
  [/^\/profile/, "Your Profile"],
  [/^\/checkout\//, "Checkout"],
  [/^\/payments\//, "Payment"],
  [/^\/lease\//, "Lease Room"],
  [/^\/become-a-lister/, "Become a Lister"],
  [/^\/lister/, "Lister Dashboard"],
  [/^\/admin/, "Admin"],
  [/^\/auth\/forgot-password$/, "Forgot Password"],
  [/^\/auth\/reset-password$/, "Reset Password"],
  [/^\/auth\/verify-email$/, "Verify Email"],
];

export default function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    // WHY: on the admin host the console is mounted at "/" so every path
    // would otherwise match the CONSUMER route tables and get marketing
    // titles. The whole host is private admin surface: one title, never
    // indexed.
    if (isAdminHost()) {
      applyMeta({ title: "Admin", path: pathname, noindex: true });
      return;
    }
    const pub = PUBLIC_ROUTES.find(([re]) => re.test(pathname));
    if (pub) {
      applyMeta({ ...pub[1], path: pathname });
      return;
    }
    const priv = PRIVATE_ROUTES.find(([re]) => re.test(pathname));
    if (priv) {
      applyMeta({ title: priv[1], path: pathname, noindex: true });
      return;
    }
    applyMeta({ title: "Page Not Found", path: pathname, noindex: true });
  }, [pathname]);

  return null;
}
